// frontend/src/pages/Services/ServiceDrawer.jsx
import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Drawer, Button, Space, Tag, Typography, Table, Empty,
  Tooltip, Popconfirm, message, Input, Tabs, Segmented,
} from 'antd';
import {
  DownloadOutlined, PlusOutlined, StopOutlined, UserAddOutlined,
} from '@ant-design/icons';
import { servicesAPI } from '../../api/client';
import LicensePurchaseModal from './LicensePurchaseModal';
import AccessAssignmentModal from './AccessAssignmentModal';
import ServiceInfoTab from './ServiceInfoTab';
import ServiceFormModal from './ServiceFormModal';
import ServiceExportModal from './ServiceExportModal';
import { fetchAllPages } from '../../utils/fetchAllPages';
import { filterAndSortAssignments } from '../../utils/serviceAssignments';
import useTableSort from '../../hooks/useTableSort';
import { usePermission } from '../../hooks/usePermission';
import {
  ALLOCATION_LABELS, LICENSE_TYPE_COLORS, LICENSE_TYPE_LABELS, formatDate,
} from './constants';

const { Text, Title } = Typography;

export default function ServiceDrawer({ service, onClose }) {
  const queryClient = useQueryClient();
  const [showAssign, setShowAssign]       = useState(false);
  const [purchaseGroup, setPurchaseGroup] = useState(null);
  const [showPurchase, setShowPurchase] = useState(false);
  const [purchaseTab, setPurchaseTab]     = useState('add');
  const [purchaseToEdit, setPurchaseToEdit] = useState(null);
  const [assignmentSearch, setAssignmentSearch] = useState('');
  const [assignmentType, setAssignmentType] = useState('all');
  const [showServiceEdit, setShowServiceEdit] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const { canEdit } = usePermission('services');
  const { sortBy, sortDir, sortIcon, getSortableHeaderProps } = useTableSort({
    initialSortBy: 'employee_name',
    initialSortDir: 'asc',
  });

  const { data: detail } = useQuery({
    queryKey: ['service-detail', service?.id],
    queryFn:  () => servicesAPI.getOne(service.id).then(r => r.data),
    enabled:  !!service?.id,
  });

  const { data: assignments } = useQuery({
    queryKey: ['service-assignments', service?.id],
    queryFn: async () => {
      const items = await fetchAllPages(({ limit, offset }) =>
        servicesAPI.getAssignments({
          service_id: service.id,
          active_only: true,
          limit,
          offset,
        }).then(r => r.data)
      );

      return {
        total: items.length,
        items,
      };
    },
    enabled: !!service?.id,
  });

  const visibleAssignments = useMemo(
    () => filterAndSortAssignments(assignments?.items, {
      search: assignmentSearch,
      licenseType: assignmentType,
      sortBy,
      sortDir,
    }),
    [assignments?.items, assignmentSearch, assignmentType, sortBy, sortDir],
  );

  const revokeMutation = useMutation({
    mutationFn: (id) => servicesAPI.revoke(id, {}),
    onSuccess: () => {
      message.success('Доступ отозван');
      queryClient.invalidateQueries(['service-assignments', service?.id]);
      queryClient.invalidateQueries(['services']);
      queryClient.invalidateQueries(['services-stats']);
    },
    onError: (e) => message.error(e?.response?.data?.detail || 'Ошибка'),
  });

  const assignmentColumns = [
    {
      title: `Сотрудник${sortIcon('employee_name')}`, dataIndex: 'employee_name', key: 'name', width: 180,
      onHeaderCell: () => getSortableHeaderProps('employee_name'),
      render: (v, r) => (
        <Space direction="vertical" size={0}>
          <Text strong style={{ fontSize: 12 }}>{v}</Text>
          {r.job_title && <Text type="secondary" style={{ fontSize: 11 }}>{r.job_title}</Text>}
        </Space>
      ),
    },
    {
      title: `Тип доступа${sortIcon('license_type')}`, dataIndex: 'license_type', key: 'license_type', width: 125,
      onHeaderCell: () => getSortableHeaderProps('license_type'),
      render: (value) => (
        <Tag color={LICENSE_TYPE_COLORS[value]} style={{ fontSize: 10, margin: 0 }}>
          {LICENSE_TYPE_LABELS[value] || value || '—'}
        </Tag>
      ),
    },
    {
      title: `Роль${sortIcon('role')}`, dataIndex: 'role', key: 'role', width: 80,
      onHeaderCell: () => getSortableHeaderProps('role'),
      render: (v) => v ? <Tag style={{ fontSize: 10 }}>{v}</Tag> : '—',
    },
    {
      title: `Выдан${sortIcon('assigned_at')}`, dataIndex: 'assigned_at', key: 'date', width: 95,
      onHeaderCell: () => getSortableHeaderProps('assigned_at'),
      render: (v) => <Text style={{ fontSize: 12 }}>{formatDate(v)}</Text>,
    },
    {
      title: '', key: 'actions', width: 40,
      render: (_, r) => (
        <Popconfirm title="Отозвать доступ?" onConfirm={() => revokeMutation.mutate(r.id)} okText="Да" cancelText="Нет">
          <Tooltip title="Отозвать">
            <Button type="text" size="small" danger icon={<StopOutlined />} />
          </Tooltip>
        </Popconfirm>
      ),
    },
  ];

  const groups = detail?.license_groups || [];
  const purchased = groups.filter(g => g.license_type === 'purchased');
  const grey      = groups.filter(g => g.license_type === 'grey');

  const licensingSummary = useMemo(() => {
    const allocationMode = detail?.allocation_mode || service?.allocation_mode || 'named_user';
    if (!purchased.length && grey.length) {
      return {
        mode: ALLOCATION_LABELS[allocationMode] || allocationMode,
        bought: '—',
        used: grey.reduce((sum, group) => sum + Number(group.used_licenses || 0), 0),
        free: '—',
      };
    }

    const bought = purchased.reduce(
      (sum, group) => sum + Number(group.total_licenses || 0),
      0,
    );
    const used = purchased.reduce(
      (sum, group) => sum + Number(group.used_licenses || 0),
      0,
    );

    return {
      mode: ALLOCATION_LABELS[allocationMode] || allocationMode,
      bought,
      used,
      free: allocationMode === 'named_user' ? Math.max(bought - used, 0) : '—',
    };
  }, [detail?.allocation_mode, service?.allocation_mode, purchased, grey]);

  const accessSummary = useMemo(() => {
    const activeAssignments = assignments?.items || [];
    const countUniqueUsers = (items) => new Set(
      items.map(item => item.employee_id).filter(Boolean),
    ).size;

    return {
      purchasedUsers: countUniqueUsers(
        activeAssignments.filter(item => item.license_type === 'purchased'),
      ),
      greyUsers: countUniqueUsers(
        activeAssignments.filter(item => item.license_type === 'grey'),
      ),
      totalUsers: countUniqueUsers(activeAssignments),
    };
  }, [assignments?.items]);

  return (
    <>
      <Drawer
        open={!!service}
        onClose={onClose}
        width={680}
        title={service ? (
          <Space>
            <Text strong>{service.name}</Text>
            <Tag color={service.category_color}>{service.category_name}</Tag>
          </Space>
        ) : ''}
        extra={(
          <Space>
            <Button
              size="small"
              icon={<DownloadOutlined />}
              onClick={() => setShowExport(true)}
            >
              Экспорт
            </Button>
            {canEdit && purchased.length <= 1 ? (
              <Button
                size="small"
                icon={<PlusOutlined />}
                onClick={() => {
                  setPurchaseGroup(purchased[0] || null);
                  setPurchaseTab('add');
                  setPurchaseToEdit(null);
                  setShowPurchase(true);
                }}
              >
                Добавить закупку
              </Button>
            ) : null}
            {canEdit && (
              <Button type="primary" size="small" icon={<UserAddOutlined />}
                onClick={() => setShowAssign(true)}>
                Выдать доступ
              </Button>
            )}
          </Space>
        )}
      >
        <Tabs
          items={[
            {
              key: 'users',
              label: 'Пользователи',
              children: (
                <>
        {/* Краткая информация о лицензировании */}
        {groups.length > 0 ? (
          <div style={{
            border: '1px solid #f0f0f0',
            borderRadius: 8,
            padding: 12,
            marginBottom: 20,
            background: '#fafafa',
          }}>
            <Text strong>Лицензирование</Text>
            <Space size={20} wrap style={{ marginTop: 8, display: 'flex' }}>
              <Text type="secondary">
                Тип лицензирования: <Text strong>{licensingSummary.mode}</Text>
              </Text>
              <Text type="secondary">
                Куплено: <Text strong>{licensingSummary.bought}</Text>
              </Text>
              <Text type="secondary">
                Выдано: <Text strong>{licensingSummary.used}</Text>
              </Text>
              <Text type="secondary">
                Свободно: <Text strong>{licensingSummary.free}</Text>
              </Text>
              <Text type="secondary">
                Грязный домен: <Text strong>{accessSummary.greyUsers}</Text>
              </Text>
              <Text type="secondary">
                Пользователей всего: <Text strong>{accessSummary.totalUsers}</Text>
              </Text>
            </Space>
          </div>
        ) : (
          <Empty
            description="Для сервиса пока не настроено лицензирование"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            style={{ marginBottom: 20 }}
          />
        )}

        {/* Активные назначения */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <Title level={5} style={{ margin: 0 }}>Активные доступы</Title>
          <Tag color="blue">{accessSummary.totalUsers}</Tag>
        </div>

        {!!assignments?.items?.length && (
          <Segmented
            value={assignmentType}
            onChange={setAssignmentType}
            options={[
              { label: `Все ${accessSummary.totalUsers}`, value: 'all' },
              { label: `Лицензии ${accessSummary.purchasedUsers}`, value: 'purchased' },
              { label: `Грязный домен ${accessSummary.greyUsers}`, value: 'grey' },
            ]}
            style={{ marginBottom: 12 }}
          />
        )}

        {!!assignments?.items?.length && (
          <Input
            allowClear
            placeholder="Поиск по имени"
            value={assignmentSearch}
            onChange={(e) => setAssignmentSearch(e.target.value)}
            style={{ marginBottom: 12 }}
          />
        )}

        {!assignments?.items?.length
          ? <Empty description="Нет активных назначений" image={Empty.PRESENTED_IMAGE_SIMPLE} />
          : !visibleAssignments.length
            ? <Empty description="Ничего не найдено" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            : <Table
                dataSource={visibleAssignments} rowKey="id"
                columns={assignmentColumns} size="small"
                pagination={{ pageSize: 20 }}
              />
        }
                </>
              ),
            },
            {
              key: 'info',
              label: 'Информация о сервисе',
              children: (
                <ServiceInfoTab
                  service={service}
                  detail={detail}
                  accessSummary={accessSummary}
                  canEdit={canEdit}
                  onEdit={() => setShowServiceEdit(true)}
                  onPurchaseOpen={(purchase) => {
                    const group = purchased.find(
                      item => item.id === purchase.license_group_id,
                    );
                    if (!group) return;
                    setPurchaseGroup(group);
                    setPurchaseTab('history');
                    setPurchaseToEdit(purchase);
                    setShowPurchase(true);
                  }}
                />
              ),
            },
          ]}
        />
      </Drawer>

      <ServiceExportModal
        open={showExport}
        onClose={() => setShowExport(false)}
        service={detail || service}
        accessSummary={accessSummary}
      />

      <ServiceFormModal
        open={showServiceEdit}
        onClose={() => setShowServiceEdit(false)}
        service={detail}
      />

      {/* Добавление лицензий и история приобретений */}
      <LicensePurchaseModal
        open={showPurchase}
        onClose={() => {
          setShowPurchase(false);
          setPurchaseGroup(null);
          setPurchaseToEdit(null);
        }}
        group={purchaseGroup}
        service={service}
        initialTab={purchaseTab}
        initialEditPurchase={purchaseToEdit}
        onSuccess={() => {
          queryClient.invalidateQueries(['service-detail', service?.id]);
          queryClient.invalidateQueries(['services']);
          queryClient.invalidateQueries(['services-stats']);
        }}
      />

      {/* Модалка выдачи доступа */}
      <AccessAssignmentModal
        open={showAssign}
        onClose={() => setShowAssign(false)}
        service={detail || service}
        licenseGroups={groups}
        onSuccess={() => {
          queryClient.invalidateQueries(['service-assignments', service?.id]);
          queryClient.invalidateQueries(['services']);
          queryClient.invalidateQueries(['services-stats']);
        }}
      />
    </>
  );
}